/**
 * v109.9.12 — Travel — unified module (Advance · Settlement · Booking · Accounts Approval)
 *
 * Tabs:
 *   1. Travel Advance          → TravelAdvancesTab (custom form + workflow)
 *   2. Advance Settlement      → RecordsPage("accounts_claims")
 *   3. Travel Booking          → <TravelBooking />
 *   4. Account team | Approval → TravelAccountsQueueTab (admins only)
 *
 * Master Admin sees a gear icon in the banner to configure the 4-user
 * Approver Pool and the amount threshold that forces the full 4-tier flow.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/GradientCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Plane, ReceiptText, MapPin, Users, Settings2, ShieldCheck, Layers, BookOpen } from "lucide-react";
import TravelBooking from "./TravelBooking";
import TravelAdvancesTab from "./TravelAdvancesTab";
import TravelAccountsQueueTab from "./TravelAccountsQueueTab";
import TravelApprovalQueueTab from "./TravelApprovalQueueTab";
import TravelAllRequestsTab from "./TravelAllRequestsTab";
import TravelSettlementsTab from "./TravelSettlementsTab";
import TravelPolicy from "./TravelPolicy";
import TravelApproverPoolDialog from "../components/TravelApproverPoolDialog";
import { useAuth } from "../context/AuthContext";
import { tabAccessibleFor, moduleCanDo } from "../lib/modules";
import api from "../lib/api";


export default function Travel() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "advance";
  const initialAction = searchParams.get("action") || "";
  const [tab, setTab] = useState(initialTab);
  const [configOpen, setConfigOpen] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  // v109.9.122 — Policy acknowledgment gate. `null` = not-yet-loaded.
  const [ackState, setAckState] = useState({ loaded: false, acknowledged: false });
  // Action pulse — a monotonically increasing counter that child tabs read to
  // know they should open their "new" dialog. Bumped every time the URL
  // ?action= param toggles on. Cleared after the child consumes it.
  const [actionPulse, setActionPulse] = useState(initialAction ? { tab: initialTab, key: initialAction, n: 1 } : null);

  const isMaster = user?.role === "master_admin";
  const isAccounts = isMaster || moduleCanDo(user, "travel", "edit");

  useEffect(() => {
    api.get("/travel-advances/am-i-approver")
      .then(({ data }) => setIsApprover(!!data?.is_approver))
      .catch(() => setIsApprover(false));
  }, []);

  // v109.9.122 — Fetch policy acknowledgment. Force-open Policy tab if not
  // acknowledged so the user cannot bypass it.
  useEffect(() => {
    api.get("/travel-policy/acknowledgment")
      .then(({ data }) => {
        const acked = !!data?.acknowledged;
        setAckState({ loaded: true, acknowledged: acked });
        if (!acked) setTab("policy");
      })
      .catch(() => setAckState({ loaded: true, acknowledged: false }));
     
  }, []);

  // React to URL changes (Dashboard quick-action navigation).
  useEffect(() => {
    const t = searchParams.get("tab");
    const a = searchParams.get("action");
    if (t && t !== tab) setTab(t);
    if (a) {
      setActionPulse({ tab: t || tab, key: a, n: Date.now() });
      // Strip ?action= from the URL so back/refresh doesn't re-trigger.
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
     
  }, [searchParams]);

  const TABS = [
    { key: "policy",     label: "Policy",             icon: BookOpen,     show: true },
    { key: "advance",    label: "Travel Advance",     icon: Plane,        show: true },
    { key: "settlement", label: "Advance Settlement", icon: ReceiptText,  show: true },
    { key: "booking",    label: "Travel Booking",     icon: MapPin,       show: true },
    { key: "approval",   label: "Approval",           icon: ShieldCheck,  show: isApprover || tab === "approval" },
    { key: "accounts",   label: "Account Team",       icon: Users,        show: isAccounts },
    { key: "all",        label: "All",                icon: Layers,       show: isMaster },
  ].filter((t) => t.show && tabAccessibleFor(user, "travel", t.key));

  // v109.9.122 — Until acknowledged, force user onto Policy tab and disable others.
  const gateActive = ackState.loaded && !ackState.acknowledged;
  const handleTabChange = (next) => {
    if (gateActive && next !== "policy") return;
    setTab(next);
  };

  return (
    <div className="max-w-[1400px] space-y-6" data-testid="travel-module">
      <PageHeader
        tone="blue"
        icon={Plane}
        eyebrow="Accounts Module"
        title="Travel"
        moduleKey="travel"
        subtitle="Manage travel advances, submit expense settlements, book official trips, and clear the accounts queue — all in one place."
        actions={
          isMaster && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfigOpen(true)}
              className="h-9 text-[12px] bg-white/90 hover:bg-white text-slate-700 border-slate-200"
              data-testid="travel-config-btn"
            >
              <Settings2 size={13} className="mr-1.5" /> Approver Pool
            </Button>
          )
        }
      />

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="flex flex-wrap gap-1.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-1.5 rounded-lg">
          {TABS.map((t) => {
            const Icon = t.icon;
            const disabled = gateActive && t.key !== "policy";
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                disabled={disabled}
                className={`data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-sm text-[12.5px] px-3 py-1.5 flex items-center gap-1.5 ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid={`travel-tab-${t.key}`}
                title={disabled ? "Acknowledge the Travel Policy first" : undefined}
              >
                <Icon size={13} />
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="policy" className="mt-4">
          <TravelPolicy onAcknowledged={() => setAckState({ loaded: true, acknowledged: true })} />
        </TabsContent>
        <TabsContent value="advance" className="mt-4">
          <TravelAdvancesTab actionPulse={actionPulse?.tab === "advance" ? actionPulse : null} />
        </TabsContent>
        <TabsContent value="settlement" className="mt-4">
          <TravelSettlementsTab actionPulse={actionPulse?.tab === "settlement" ? actionPulse : null} />
        </TabsContent>
        <TabsContent value="booking" className="mt-4">
          <TravelBooking embedded actionPulse={actionPulse?.tab === "booking" ? actionPulse : null} />
        </TabsContent>
        {(isApprover || tab === "approval") && (
          <TabsContent value="approval" className="mt-4">
            <TravelApprovalQueueTab />
          </TabsContent>
        )}
        {isAccounts && tabAccessibleFor(user, "travel", "accounts") && (
          <TabsContent value="accounts" className="mt-4">
            <TravelAccountsQueueTab />
          </TabsContent>
        )}
        {isMaster && tabAccessibleFor(user, "travel", "all") && (
          <TabsContent value="all" className="mt-4">
            <TravelAllRequestsTab />
          </TabsContent>
        )}
      </Tabs>

      <TravelApproverPoolDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
      />
    </div>
  );
}
